// Role-based access control configuration
// Based on the role requirements document

// User Roles
export const USER_ROLES = {
  ADMIN: 'admin',
  SUPER_ADMIN: 'super_admin',
  MANAGER: 'manager',
  CASHIER: 'cashier',
  BARISTA: 'barista'
}

// Permission Categories
export const PERMISSIONS = {
  // Dashboard & Overview
  DASHBOARD: {
    VIEW: 'dashboard.view'
  },

  // Products & Inventory
  PRODUCTS: {
    VIEW: 'products.view',
    CREATE: 'products.create',
    EDIT: 'products.edit',
    DELETE: 'products.delete',
    MANAGE_CATEGORIES: 'products.manage_categories',
    MANAGE_UNITS: 'products.manage_units',
    VIEW_INVENTORY: 'products.view_inventory',
    MANAGE_RAW_MATERIALS: 'products.manage_raw_materials',
    VIEW_RAW_INVENTORY: 'products.view_raw_inventory',
    MANAGE_RECIPES: 'products.manage_recipes',
    VIEW_STOCK_ALERTS: 'products.view_stock_alerts'
  },

  // Tables
  TABLES: {
    VIEW: 'tables.view',
    CREATE: 'tables.create',
    EDIT: 'tables.edit',
    DELETE: 'tables.delete'
  },

  // Customers
  CUSTOMERS: {
    VIEW_ALL: 'customers.view_all', // All customers across all branches
    VIEW_BRANCH: 'customers.view_branch', // Only customers for specific branch
    CREATE: 'customers.create',
    EDIT: 'customers.edit',
    DELETE: 'customers.delete',
    MANAGE_GROUPS: 'customers.manage_groups'
  },

  // Suppliers
  SUPPLIERS: {
    VIEW: 'suppliers.view',
    CREATE: 'suppliers.create',
    EDIT: 'suppliers.edit',
    DELETE: 'suppliers.delete'
  },

  // Users & Roles
  USERS: {
    VIEW: 'users.view',
    CREATE: 'users.create',
    EDIT: 'users.edit',
    DELETE: 'users.delete',
    RESET_PASSWORD: 'users.reset_password',
    MANAGE_ROLES: 'users.manage_roles'
  },

  // Branches
  BRANCHES: {
    VIEW: 'branches.view',
    CREATE: 'branches.create',
    EDIT: 'branches.edit',
    DELETE: 'branches.delete',
    SWITCH: 'branches.switch' // Can switch between branches
  },

  // Purchase Orders
  PURCHASE_ORDERS: {
    VIEW: 'purchase_orders.view',
    CREATE: 'purchase_orders.create',
    EDIT: 'purchase_orders.edit',
    DELETE: 'purchase_orders.delete',
    APPROVE: 'purchase_orders.approve'
  },

  // GRN (Goods Received Notes)
  GRN: {
    VIEW: 'grn.view',
    CREATE: 'grn.create',
    EDIT: 'grn.edit',
    DELETE: 'grn.delete',
    MANAGE_PAYMENTS: 'grn.manage_payments'
  },

  // Stock Management
  STOCK: {
    VIEW_RAW_MATERIALS: 'stock.view_raw_materials',
    VIEW_FINISHED_PRODUCTS: 'stock.view_finished_products',
    TRANSFER_PRODUCTS: 'stock.transfer_products'
  },

  // Promotions
  PROMOTIONS: {
    VIEW: 'promotions.view',
    CREATE: 'promotions.create',
    EDIT: 'promotions.edit',
    DELETE: 'promotions.delete'
  },

  // Reports
  REPORTS: {
    VIEW: 'reports.view',
    EXPORT: 'reports.export'
  },

  // System Configuration
  SYSTEM_CONFIG: {
    VIEW: 'system_config.view',
    MANAGE: 'system_config.manage'
  },

  // POS
  POS: {
    ACCESS: 'pos.access',
    PROCESS_ORDERS: 'pos.process_orders',
    SWITCH_BRANCH: 'pos.switch_branch'
  },

  // Barista
  BARISTA: {
    ACCESS: 'barista.access',
    VIEW_ORDERS: 'barista.view_orders',
    UPDATE_STATUS: 'barista.update_status'
  },

  // HR
  HR: {
    VIEW_JOBS: 'hr.view_jobs',
    MANAGE_JOBS: 'hr.manage_jobs',
    VIEW_EMPLOYEES: 'hr.view_employees',
    MANAGE_EMPLOYEES: 'hr.manage_employees',
    VIEW_SHIFTS: 'hr.view_shifts',
    MANAGE_SHIFTS: 'hr.manage_shifts',
    VIEW_EMPLOYEE_SHIFTS: 'hr.view_employee_shifts',
    MANAGE_EMPLOYEE_SHIFTS: 'hr.manage_employee_shifts',
    VIEW_ATTENDANCE: 'hr.view_attendance',
    MANAGE_ATTENDANCE: 'hr.manage_attendance',
    VIEW_LEAVE_TYPES: 'hr.view_leave_types',
    MANAGE_LEAVE_TYPES: 'hr.manage_leave_types',
    VIEW_LEAVES: 'hr.view_leaves',
    MANAGE_LEAVES: 'hr.manage_leaves',
    VIEW_PENDING_LEAVES: 'hr.view_pending_leaves',
    VIEW_TARGETS: 'hr.view_targets',
    MANAGE_TARGETS: 'hr.manage_targets',
    VIEW_ALLOWANCES: 'hr.view_allowances',
    MANAGE_ALLOWANCES: 'hr.manage_allowances',
    VIEW_PRE_PAYMENTS: 'hr.view_pre_payments',
    MANAGE_PRE_PAYMENTS: 'hr.manage_pre_payments',
    VIEW_SALARY: 'hr.view_salary',
    MANAGE_SALARY: 'hr.manage_salary',
    VIEW_HRM_SETTINGS: 'hr.view_hrm_settings',
    MANAGE_HRM_SETTINGS: 'hr.manage_hrm_settings'
  },

  // Shifts
  SHIFTS: {
    OPEN_CLOSE: 'shifts.open_close'
  }
}

// Role-based permissions mapping
export const ROLE_PERMISSIONS = {
  [USER_ROLES.ADMIN]: [
    // Admin & Super Admin can handle all functions & features
    // Can not create open & close shift
    ...Object.values(PERMISSIONS.DASHBOARD),
    ...Object.values(PERMISSIONS.PRODUCTS),
    ...Object.values(PERMISSIONS.TABLES),
    ...Object.values(PERMISSIONS.CUSTOMERS),
    ...Object.values(PERMISSIONS.SUPPLIERS),
    ...Object.values(PERMISSIONS.USERS),
    ...Object.values(PERMISSIONS.BRANCHES),
    ...Object.values(PERMISSIONS.PURCHASE_ORDERS),
    ...Object.values(PERMISSIONS.GRN),
    ...Object.values(PERMISSIONS.STOCK),
    ...Object.values(PERMISSIONS.PROMOTIONS),
    ...Object.values(PERMISSIONS.REPORTS),
    ...Object.values(PERMISSIONS.POS),
    ...Object.values(PERMISSIONS.BARISTA),
    ...Object.values(PERMISSIONS.HR)
    // Note: Admin cannot create open & close shift (excluded SHIFTS.OPEN_CLOSE)
  ],

  [USER_ROLES.SUPER_ADMIN]: [
    // Same as admin
    ...Object.values(PERMISSIONS.DASHBOARD),
    ...Object.values(PERMISSIONS.PRODUCTS),
    ...Object.values(PERMISSIONS.TABLES),
    ...Object.values(PERMISSIONS.CUSTOMERS),
    ...Object.values(PERMISSIONS.SUPPLIERS),
    ...Object.values(PERMISSIONS.USERS),
    ...Object.values(PERMISSIONS.BRANCHES),
    ...Object.values(PERMISSIONS.PURCHASE_ORDERS),
    ...Object.values(PERMISSIONS.GRN),
    ...Object.values(PERMISSIONS.STOCK),
    ...Object.values(PERMISSIONS.PROMOTIONS),
    ...Object.values(PERMISSIONS.REPORTS),
    ...Object.values(PERMISSIONS.POS),
    ...Object.values(PERMISSIONS.BARISTA),
    ...Object.values(PERMISSIONS.HR)
  ],

  [USER_ROLES.MANAGER]: [
    // Can access those for their branch only
    PERMISSIONS.DASHBOARD.VIEW,
    
    // Products
    PERMISSIONS.PRODUCTS.VIEW,
    PERMISSIONS.PRODUCTS.VIEW_INVENTORY,
    // PERMISSIONS.PRODUCTS.MANAGE_RAW_MATERIALS, // Removed - only admin/super_admin can manage raw materials
    PERMISSIONS.PRODUCTS.VIEW_RAW_INVENTORY,
    PERMISSIONS.PRODUCTS.VIEW_STOCK_ALERTS,
    PERMISSIONS.TABLES.VIEW,

    // Customers (all customers in all branches)
    PERMISSIONS.CUSTOMERS.VIEW_ALL,

    // Customer Groups (disable create group button & edit option)
    PERMISSIONS.CUSTOMERS.VIEW_BRANCH,

    // Suppliers (view & filter all suppliers, cannot create supplier)
    PERMISSIONS.SUPPLIERS.VIEW,

    // Role & Permission
    // Users - Can create, edit, delete, and view all users related to their branch only
    PERMISSIONS.USERS.VIEW,
    PERMISSIONS.USERS.CREATE,
    PERMISSIONS.USERS.EDIT,
    PERMISSIONS.USERS.DELETE,

    // Purchase Order
    PERMISSIONS.PURCHASE_ORDERS.VIEW,
    PERMISSIONS.PURCHASE_ORDERS.CREATE,

    // GRN
    PERMISSIONS.GRN.VIEW,
    PERMISSIONS.GRN.CREATE,
    PERMISSIONS.GRN.MANAGE_PAYMENTS,

    // Promotions (view only)
    PERMISSIONS.PROMOTIONS.VIEW,

    // Reports (access all reports and functionalities related to their branch)
    PERMISSIONS.REPORTS.VIEW,

    // System Configuration (view system configurations for POS functionality)
    PERMISSIONS.SYSTEM_CONFIG.VIEW,

    // POS Screen (access all POS functionalities without Open/Close shifts)
    PERMISSIONS.POS.ACCESS,
    PERMISSIONS.POS.PROCESS_ORDERS,

    // Barista Screen (access barista interface for order management)
    PERMISSIONS.BARISTA.ACCESS,
    PERMISSIONS.BARISTA.VIEW_ORDERS,
    PERMISSIONS.BARISTA.UPDATE_STATUS
  ],

  [USER_ROLES.CASHIER]: [
    // Can access POS screens and details related to their branch
    // Cannot access switch branch (only cashier branch)
    PERMISSIONS.POS.ACCESS,
    PERMISSIONS.POS.PROCESS_ORDERS
  ],

  [USER_ROLES.BARISTA]: [
    // Can access POS for order taking and barista screen for order management
    // Cannot access other branch (only for their branch)
    PERMISSIONS.POS.ACCESS,
    PERMISSIONS.POS.PROCESS_ORDERS,
    PERMISSIONS.BARISTA.ACCESS,
    PERMISSIONS.BARISTA.VIEW_ORDERS,
    PERMISSIONS.BARISTA.UPDATE_STATUS
  ]
}

// Branch-based restrictions
export const BRANCH_RESTRICTIONS = {
  [USER_ROLES.MANAGER]: true,  // Limited to their branch
  [USER_ROLES.CASHIER]: true,  // Limited to their branch  
  [USER_ROLES.BARISTA]: true   // Limited to their branch
}

// Helper functions
export const hasPermission = (userRole, permission) => {
  if (!userRole || !permission) return false
  
  const permissions = ROLE_PERMISSIONS[userRole] || []
  return permissions.includes(permission)
}

export const hasAnyPermission = (userRole, permissions) => {
  if (!userRole || !permissions || !Array.isArray(permissions)) return false
  
  return permissions.some(permission => hasPermission(userRole, permission))
}

export const hasAllPermissions = (userRole, permissions) => {
  if (!userRole || !permissions || !Array.isArray(permissions)) return false
  
  return permissions.every(permission => hasPermission(userRole, permission))
}

export const isBranchRestricted = (userRole) => {
  return BRANCH_RESTRICTIONS[userRole] || false
}

export const canAccessAllBranches = (userRole) => {
  return userRole === USER_ROLES.ADMIN || userRole === USER_ROLES.SUPER_ADMIN
}

export const getRoleDisplayName = (role) => {
  const roleNames = {
    [USER_ROLES.ADMIN]: 'Admin',
    [USER_ROLES.SUPER_ADMIN]: 'Super Admin',
    [USER_ROLES.MANAGER]: 'Manager',
    [USER_ROLES.CASHIER]: 'Cashier',
    [USER_ROLES.BARISTA]: 'Barista'
  }
  
  return roleNames[role] || role
}

export const isAdminRole = (role) => {
  return role === USER_ROLES.ADMIN || role === USER_ROLES.SUPER_ADMIN
}

export const isManagerRole = (role) => {
  return role === USER_ROLES.MANAGER
}

export const isCashierRole = (role) => {
  return role === USER_ROLES.CASHIER
}

export const isBaristaRole = (role) => {
  return role === USER_ROLES.BARISTA
}
